import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Section";
import ZoomImage from "@/components/ZoomImage";
import { SEO } from "@/components/SEO";
import { PAGE_SEO } from "@/lib/seoData";

function TimerCompare() {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.5, once: true });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let n = 0;
    const id = setInterval(() => {
      n += 1;
      setCount(n);
      if (n >= 35) clearInterval(id);
    }, 180);
    return () => clearInterval(id);
  }, [inView]);
  const badge =
    "absolute top-5 font-mono tabular-nums px-5 py-4 border bg-black/75 backdrop-blur-sm";
  return (
    <figure ref={ref} className="relative mt-4 border border-zinc-800 bg-[#0e0e0e] overflow-hidden" data-testid="why-timer-compare">
      <img loading="lazy" src={IMG_BAG} alt="Left: searching a bag. Right: one press on the blue face." className="w-full aspect-[16/10] object-cover" />
      {/* Slow, ticking search time on the left */}
      <div className={`${badge} left-5 border-zinc-600 text-zinc-300`}>
        <span className="block font-mono text-[11px] md:text-xs tracking-[0.28em] uppercase text-zinc-400 mb-1.5">The old way</span>
        <span className="text-4xl md:text-6xl font-light leading-none">{String(count).padStart(2, "0")}</span>
        <span className="text-sm ml-1.5 text-zinc-500">sec</span>
      </div>
      {/* Instant wrist action on the right */}
      <div className={`${badge} right-5 border-accent text-accent`}>
        <span className="block font-mono text-[11px] md:text-xs tracking-[0.28em] uppercase text-accent/80 mb-1.5">The new way</span>
        <span className="text-4xl md:text-6xl font-light leading-none">01</span>
        <span className="text-sm ml-1.5 text-accent/70">sec</span>
      </div>
      {/* Why it matters, old vs new, side by side */}
      <figcaption className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-800 border-t border-zinc-800">
        <div className="bg-[#0e0e0e] p-6 md:p-8">
          <span className="block font-mono text-[11px] tracking-[0.28em] uppercase text-zinc-500 mb-3">The old way</span>
          <p className="font-body text-zinc-400 text-base leading-relaxed">
            Stop mid-day. Set your bag down. Dig past everything else,
            touching surface after surface inside before you even reach the
            sanitizer, potentially spreading what you were trying to avoid.
            Apply. Repack. Then pick your day back up.
          </p>
        </div>
        <div className="bg-[#0e0e0e] p-6 md:p-8">
          <span className="block font-mono text-[11px] tracking-[0.28em] uppercase text-accent mb-3">The new way</span>
          <p className="font-body text-zinc-200 text-base leading-relaxed">
            One press on your wrist. No searching, no setting things down,
            nothing else to touch.{" "}
            <span className="text-accent">You just keep moving.</span>
          </p>
        </div>
      </figcaption>
    </figure>
  );
}

const VILLAINS = [
  {
    title: "Inconvenience",
    body: "The customer has to stop what they are doing, locate the product, open it, apply it, put it away, and continue.",
  },
  {
    title: "Out of sight, out of mind",
    body: "If the product is not visible and immediately available, people forget to use it or reapply it.",
  },
  {
    title: "The interruption",
    body: "Traditional personal care asks the customer to interrupt life in order to protect themselves.",
  },
  {
    title: "False preparedness",
    body: "Owning sunscreen or sanitizer does not mean you have access to it when you need it.",
  },
];

const IMG_BAG =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/rq6xmaiy_looking%20bag.jpg";
const IMG_FOUR =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/p5jt86xp_4%20wearing.jpg";

const WA = "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/";
const HERO_SLIDES = [
  { src: `${WA}r0hpextd_lake%20lady%202.jpg`, alt: "A woman by a lake applying sunscreen while checking her phone, a magenta WearSpray band on her wrist" },
  { src: `${WA}pejuwhnw_runner%20banner.jpg`, alt: "A marathon runner mid-race wearing a WearSpray band on his wrist" },
  { src: `${WA}0txx7m4f_woman%20on%20the%20move%20banner.jpg`, alt: "A woman moving briskly through a city crowd, a magenta WearSpray band on her wrist" },
  { src: `${WA}2hqnnwlg_student%20banner.jpg`, alt: "A university student crossing a sunlit campus wearing a blue WearSpray band" },
];

function HeroCrossfade() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((p) => (p + 1) % HERO_SLIDES.length), 4500);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="absolute inset-0" data-testid="why-hero-crossfade">
      {HERO_SLIDES.map((s, idx) => (
        <img loading="lazy"
          key={s.src}
          src={s.src}
          alt={s.alt}
          className="absolute inset-0 w-full h-full object-cover object-[center_28%] transition-opacity duration-1000 ease-in-out"
          style={{ opacity: idx === i ? 1 : 0 }}
          data-testid={`why-hero-slide-${idx}`}
        />
      ))}
    </div>
  );
}

const CATEGORIES = [
  "Personal Care",
  "Skincare",
  "Sun Care",
  "Insect Protection",
  "Sanitization",
  "Fragrance",
  "Wellness",
  "Healthcare",
  "Sports",
  "Travel",
  "Hospitality",
];

function Question({ n, title, children }) {
  return (
    <section
      className="border-t border-zinc-800"
      data-testid={`why-q-${n}`}
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <Reveal>
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-accent block mb-6">
            {String(n).padStart(2, "0")}
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-[1.1] text-white max-w-3xl">
            {title}
          </h2>
        </Reveal>
        <div className="mt-10 space-y-6">{children}</div>
      </div>
    </section>
  );
}

function Body({ children, className = "" }) {
  return (
    <Reveal>
      <p className={`font-body text-zinc-300 text-lg leading-relaxed max-w-2xl ${className}`}>
        {children}
      </p>
    </Reveal>
  );
}

function Punch({ children }) {
  return (
    <Reveal delay={0.05}>
      <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light tracking-tight leading-[1.2] text-accent max-w-3xl">
        {children}
      </p>
    </Reveal>
  );
}

export default function WhyWearSpray() {
  return (
    <div data-testid="why-page">
      <SEO {...PAGE_SEO["/why"]} path="/why" />
      {/* HERO BANNER, four-person composite */}
      <section
        className="relative w-full h-[78vh] min-h-[540px] overflow-hidden bg-black"
        data-testid="why-hero-banner"
      >
        <HeroCrossfade />
        {/* Legibility scrims */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/40 to-[#0a0a0a]/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/85 via-transparent to-transparent" />

        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-6 md:px-12 w-full pb-14 md:pb-20">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-5"
              data-testid="why-hero"
            >
              Why WearSpray
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-heading text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter leading-[1.03] max-w-4xl"
            >
              Because the Best Protection Is the One You{" "}
              <span className="text-accent">Always Have On.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="font-body text-base md:text-xl text-zinc-200 mt-6 max-w-2xl leading-relaxed"
            >
              Accessible the instant you need it. Easy to remember. Fast to
              apply. So you stay protected, prepared, and confident, without
              breaking your stride.
            </motion.p>
          </div>
        </div>
      </section>

      {/* PRODUCT SHOT, under hero banner */}
      <section className="border-t border-zinc-800 bg-[#0b0b0b]" data-testid="why-product-shot">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div className="relative flex items-center justify-center">
              <div
                className="pointer-events-none absolute inset-0 blur-3xl opacity-40"
                style={{ background: "radial-gradient(circle, #ff2d7855, transparent 68%)" }}
              />
              <img loading="lazy"
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/4b1mmnpx_Cleaned%20Silicone%20Band%20on%20Black%20Background.webp"
                alt="WearSpray silicone band with the pressure-actuated reservoir glowing"
                className="relative w-full max-w-md max-h-[70vh] object-contain hue-cycle"
                data-testid="why-product-image"
              />
            </div>
            <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-zinc-500 text-center mt-5" data-testid="why-product-caption">
              Silicone Band &middot; <span className="text-accent">Endless possibilities of shape and style</span>
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="font-mono text-xs tracking-[0.24em] uppercase text-accent block mb-4">
              The Wearable
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight leading-tight text-white max-w-md">
              One press. Whatever you need, right on your wrist.
            </h2>
            <div className="flex items-end gap-8 mt-8" data-testid="why-one-second">
              <div>
                <span className="block font-heading text-6xl md:text-7xl font-light text-accent leading-[0.9]">
                  1<span className="text-3xl md:text-4xl align-top ml-1">sec</span>
                </span>
                <span className="block font-mono text-[11px] tracking-[0.2em] uppercase text-white/90 mt-3">
                  Press, and it&rsquo;s in your palm
                </span>
              </div>
              <div className="pb-1">
                <span className="block font-heading text-3xl md:text-4xl font-light text-zinc-600 leading-none line-through decoration-1">
                  30<span className="text-xl align-top ml-1">sec</span>
                </span>
                <span className="block font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-500 mt-3">
                  Rummaging through a bag
                </span>
              </div>
            </div>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-8 max-w-md">
              A refillable reservoir and a pressure-actuated breathing valve turn
              the wrist into an immediate point of access, fill it with the
              formula that fits your day, then dispense on demand with a single press.
            </p>
          </Reveal>
        </div>
      </section>


      {/* THE DIFFERENCE · ABILITY TO ACT */}
      <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="why-ability-to-act">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-6">
            The Difference
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-[1.1] text-white max-w-4xl">
            Everyone can sense. Everyone has an app.{" "}
            <span className="text-accent">Only WearSpray lets you act.</span>
          </h2>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-8 max-w-3xl">
            Biometric sensing is everywhere. Smart bands, smart sensors and AI
            apps already tell us what is happening and what it means, and
            they will only get smarter. But knowing is not doing. The one thing
            none of them can do is act.
          </p>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-4 max-w-3xl">
            That is WearSpray. As the sensor and the app come together, WearSpray
            builds an ecosystem around your skin, your preferences and your
            choices, then does what no other wearable can: instantly
            deliver the right care the moment it matters. Wearables are learning
            what to do. WearSpray already lets you do it.
          </p>
        </div>
      </section>

      {/* HEALTH IS THE NEW BEAUTY, statement */}
      <section className="relative border-t border-zinc-800 bg-black overflow-hidden" data-testid="why-health-beauty">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70vw] h-[50vh] radial-glow opacity-20 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-28 md:py-40 text-center">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-8">
              Our Belief
            </p>
            <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light tracking-tight leading-[1.25] text-zinc-200">
              Protect your skin&rsquo;s health, and its beauty follows.
            </p>
            <p className="font-heading text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter leading-[1.05] text-accent mt-8">
              Health is the new beauty.
            </p>
            <div className="mx-auto mt-12 h-px w-24 bg-gradient-to-r from-transparent via-accent to-transparent" />
          </Reveal>
        </div>
      </section>

      {/* HERO */}
      <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 overflow-hidden border-t border-zinc-800">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[120vw] h-[55vh] radial-glow opacity-50 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12">
          <motion.h2
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter leading-[1.02]"
          >
            Why the wrist?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="font-heading text-xl sm:text-2xl md:text-3xl font-light text-zinc-200 mt-8 leading-snug max-w-3xl"
          >
            Because the best personal care solution is the one you can reach
            when you actually need it.
          </motion.p>
          <div className="mt-10 space-y-6">
            <p className="font-body text-zinc-400 text-lg leading-relaxed max-w-2xl">
              Sunscreen, sanitizer, insect repellent, skincare, and other topical
              products are often left in bags, cars, backpacks, lockers, or at
              home. WearSpray moves access to the wrist, creating an immediate
              point of use that travels with you throughout the day.
            </p>
            <p className="font-body text-zinc-400 text-lg leading-relaxed max-w-2xl">
              The wrist is already where people wear technology, track activity,
              receive information, and interact with the world around them.
            </p>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="font-body text-zinc-300 text-lg leading-relaxed max-w-2xl mt-8"
          >
            So when what you need is already on your wrist, you apply it the
            instant you need it. No searching. No bottle. No pause, you never
            break your stride.
          </motion.p>

          <div className="mt-10 border-l-2 border-accent pl-6 md:pl-8 max-w-3xl">
            <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light tracking-tight leading-[1.15] text-white">
              You feel prepared. You feel confident.
            </p>
            <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light tracking-tight leading-[1.15] text-accent mt-2">
              And you keep moving.
            </p>
          </div>

          <p className="font-body text-zinc-400 text-lg leading-relaxed max-w-2xl mt-8">
            We believe the wrist can also become the next point of access for
            personal care.
          </p>
        </div>
      </section>

      {/* 01 · THE VILLAINS */}
      <section className="border-t border-zinc-800" data-testid="why-villains">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-accent block mb-6">
              01 · What we are up against
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-[1.1] text-white max-w-3xl">
              What gets in the way of protecting yourself?
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
            {VILLAINS.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.05}>
                <div
                  className="group h-full bg-[#0d0d0d] p-8 md:p-10 hover:bg-[#111] transition-colors"
                  data-testid={`why-villain-${i + 1}`}
                >
                  <span className="font-mono text-xs tracking-[0.28em] uppercase text-zinc-600 block mb-5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-heading text-xl md:text-2xl font-light tracking-tight text-white leading-snug">
                    {v.title}
                  </h3>
                  <p className="font-body text-zinc-400 text-base md:text-lg leading-relaxed mt-4">
                    {v.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light tracking-tight leading-[1.2] text-accent max-w-3xl mt-14">
              WearSpray was built to defeat every one of them.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 02 · ACCESSIBILITY */}
      <Question n={2} title="Why does accessibility change behavior?">
        <Body>
          People are more likely to use a solution when it is immediately
          available.
        </Body>
        <Body>
          Reapplying sunscreen, sanitizing after contact, using insect
          repellent, or reaching for skincare often depends less on intention
          than on access at the right moment.
        </Body>
        <Reveal>
          <div className="border-l-2 border-accent pl-6 md:pl-8">
            <p className="font-heading text-xl md:text-2xl font-light text-white leading-snug">
              WearSpray removes the interruption.
            </p>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-4">
              No searching through a bag. No returning to the car. No bottle to
              remember.
            </p>
          </div>
        </Reveal>
        <Punch>Press. Apply. Keep moving.</Punch>
        <Reveal delay={0.05}>
          <TimerCompare />
        </Reveal>
        <Body className="text-zinc-400">
          By making personal care easier to access, WearSpray has the potential
          to turn occasional use into a more natural daily habit.
        </Body>
      </Question>

      {/* 03 · PLATFORM ACROSS INDUSTRIES */}
      <Question n={3} title="Why can one platform serve multiple industries?">
        <Body>Because WearSpray is not built around a single formulation.</Body>
        <Body>
          The patented dispensing architecture is designed as a wearable
          platform for carrying and applying liquids, lotions, and gels
          directly from the wrist.
        </Body>
        <Reveal>
          <div className="flex flex-wrap gap-3 max-w-3xl" data-testid="why-categories">
            {CATEGORIES.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-2.5 font-mono text-sm text-zinc-300 border border-zinc-800 bg-[#121212] px-4 py-3"
              >
                <span className="w-1.5 h-1.5 bg-[#00e5ff] shrink-0" />
                {c}
              </span>
            ))}
          </div>
        </Reveal>
        <Body className="text-zinc-400">
          Different industries may require different formulations, designs,
          partnerships, or applications. The fundamental need remains the same.
        </Body>
        <Reveal>
          <div
            className="border-l-2 border-accent pl-6 md:pl-8 max-w-3xl"
            data-testid="why-allergy-example"
          >
            <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-4">
              For example · Allergy &amp; OTC skin relief
            </span>
            <p className="font-body text-zinc-300 text-lg leading-relaxed">
              The same wrist&#8209;worn architecture that carries sunscreen or
              sanitizer can just as easily hold a topical antihistamine, a
              hydrocortisone cream, or a soothing barrier mist, ready to
              calm a sudden flare&#8209;up the instant it starts, without stopping
              to hunt for a squeezed&#8209;out tube.
            </p>
          </div>
        </Reveal>
        <Punch>Put the solution where the person can reach it.</Punch>
      </Question>

      {/* 04 · CATEGORY OWNERSHIP */}
      <Question n={4} title="Why is WearSpray positioned to own this category?">
        <Body>
          Because WearSpray is not simply an idea.
        </Body>
        <Reveal>
          <div
            className="border-l-2 border-accent pl-6 md:pl-8 max-w-3xl"
            data-testid="why-patents-callout"
          >
            <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent flex items-center gap-2.5 mb-4">
              <ShieldCheck size={15} /> Issued Patents
            </span>
            <p className="font-heading text-xl md:text-2xl font-light tracking-tight text-white leading-snug">
              We hold issued patents protecting wearable dispensing and its
              evolution into broader wearable and smartwatch integration.
            </p>
          </div>
        </Reveal>
        <Reveal>
          <div className="border-l-2 border-accent pl-6 md:pl-8 space-y-1">
            {[
              "We engineered it.",
              "We manufactured it.",
              "We brought it to market.",
              "We proved that wearable dispensing can work in the real world.",
            ].map((t) => (
              <p
                key={t}
                className="font-heading text-2xl md:text-3xl font-light tracking-tight text-white leading-tight"
              >
                {t}
              </p>
            ))}
          </div>
        </Reveal>
        <Body>
          What began with one simple question, <span className="text-zinc-200 italic">"Why am I carrying this when I could be wearing it?"</span>, has evolved into something much larger.
        </Body>
        <Reveal>
          <div className="pt-2">
            <p className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight text-white leading-[1.12] max-w-3xl">
              A <span className="text-accent">patented platform</span> for moving
              personal care
            </p>
            <p className="font-heading text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter text-accent mt-2">
              from carried to instantly within reach.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <Link
            to="/contact"
            data-testid="why-cta"
            className="group inline-flex items-center gap-2 font-body text-sm font-medium bg-[#00e5ff] text-black px-8 py-4 mt-6 hover:bg-[#00b3cc] transition-colors"
          >
            Partner With Us
            <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </Reveal>
      </Question>

      {/* WHAT'S NEXT · PILOT INVITATION */}
      <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="why-pilot">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-6">
            What&rsquo;s Next
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-[1.1] text-white max-w-4xl">
            The delivery platform is already developed. The next phase is{" "}
            <span className="text-accent">intelligent integration.</span>
          </h2>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-8 max-w-3xl">
            We are currently building the WearSpray app and have identified the
            sensor technology for integration. The next step is to bring the
            wearable dispenser, sensor and software together into a unified
            working system.
          </p>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-4 max-w-3xl">
            We are now seeking a strategic partner to participate in a pilot
            program evaluating WearSpray in real world and, where appropriate,
            clinical settings. The goal is to test the complete experience, gather
            meaningful user and performance data, and help shape the next
            generation of actionable personal care.
          </p>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-4 max-w-3xl">
            For organizations interested in exploring how wearable intelligence can
            move beyond monitoring and into immediate physical action, we would
            welcome the opportunity to collaborate.
          </p>
          <Link
            to="/contact"
            data-testid="why-pilot-cta"
            className="group inline-flex items-center gap-2 font-body text-sm font-medium bg-[#00e5ff] text-black px-8 py-4 mt-8 hover:bg-[#00b3cc] transition-colors"
          >
            Start a Pilot Conversation
            <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
